<script lang="ts">
    import {
        AppState,
        type Category,
        type DetailQuery,
        type DetailResponse, type MeResponse,
        type SearchQuery,
        type SearchResponse, type SortOption
    } from "./lib/NvbsSearchEngineApi/interfaces";

    import {
        setUser as setSentryUser
    } from "@sentry/svelte";

    import ErrorMessage from "./lib/components/ErrorMessage.svelte";
    import Loading from "./lib/components/Loading.svelte";
    import {onMount, setContext, tick} from "svelte";
    import {NvbsSearchEngineApi} from "./lib/NvbsSearchEngineApi/NvbsSearchEngineApi";
    import Searchbar from "./lib/Searchbar.svelte";
    import CategoryBlocks from "./lib/components/CategoryBlocks.svelte";
    import SearchTerms from "./lib/components/SearchTerms.svelte";
    import Post from "./lib/common/Post.svelte";
    import Pagination from "./lib/Pagination.svelte";
    import PostDetail from "./lib/PostDetail.svelte";
    import {SearchEngineUrlState, type UrlState} from "./lib/common/SearchEngineUrlState";
    import LayoutSelector from "./lib/components/LayoutSelector.svelte";
    import {LayoutType} from "./lib/common/types";
    import SortSelector from "./lib/components/SortSelector.svelte";
    import {GetThumbnailSize, ImageSizePresets} from "./lib/common/ImageSizePresets";
    import {canShareCheck, mobileCheck} from "./lib/common/functions";

    let {url, token, initialState, attributes}: {
        url: string;
        token: string;
        initialState: UrlState;
        attributes?: {
            title: string;
            infourl: string;
            copyrighturl: string;
        }

    } = $props();
    let layout = $state<LayoutType>(initialState.layout);

    let searchStore = $state<SearchQuery>(initialState.search);

    let appState: AppState = $state(AppState.Loading);

    let categories: Category[] = $state([]);
    let selectedCategory: Category | null = $state(null);
    let searchResult: SearchResponse | null = $state(null);
    let loading = $state(false);

    let detailResponse = $state<DetailResponse | null>(null);

    let currentDetailIndex = $state<number | null>(null);
    let currentDetailHasPrev = $state(false);
    let currentDetailHasNext = $state(false);

    let currentUser = $state<MeResponse>();

    let sort_options = $state<SortOption[]>([]);

    let thumbnailSize = $state(ImageSizePresets.default);

    let scrollState = $state({
        x: 0,
        y: 0
    });

    const saveScrollstate = () => {

        if(scrollState.x !== 0 || scrollState.y !== 0) {
            return;
        }

        scrollState.x = window.scrollX;
        scrollState.y = window.scrollY;
    }

    const restoreScrollstate = () => {
        window.scrollTo({
            top: scrollState.y,
            left: scrollState.x,
            behavior: 'instant'
        });

        scrollState.x = 0;
        scrollState.y = 0;
    }

    setContext("is-mobile", mobileCheck());
    setContext("can-share", canShareCheck());


    async function getSortOptions(
        category: string|null|undefined
    ) {
        if (category) {
            sort_options = await NvbsSearchEngineApi.getSorts(category)
        } else {
            sort_options = [];
            searchStore.sort = undefined;
        }
    }

    $effect(() => {
        if(appState !== AppState.Success) {
            return;
        }

        getSortOptions(
            selectedCategory ? selectedCategory.name : null
        )
    })


    // Run a debugger everytime the searchStore changes


    onMount(async () => {
        NvbsSearchEngineApi.init(url, token);

        currentUser = await NvbsSearchEngineApi.getSignedInUser();
        await setUserForLogging(currentUser);

        categories = await NvbsSearchEngineApi.getCategories();
        if (searchStore.category) {
            selectedCategory = categories.find(category => category.name === searchStore.category) || null;
        }

        await getSortOptions(searchStore.category); // fetch sort options for the selected category

        await tick();
        appState = AppState.Success;
        await tick();
        searchStore.page = initialState.search.page;
        if (initialState.detailIndex) {
            currentDetailIndex = initialState.detailIndex;
        }

        window.addEventListener('popstate', () => {
            const urlState = SearchEngineUrlState.Get();
            if (urlState) {
                layout = urlState.layout;
                searchStore = urlState.search;
                if (urlState.detailIndex) {
                    currentDetailIndex = urlState.detailIndex;
                } else {
                    currentDetailIndex = null;
                }
                selectedCategory = categories.find(category => category.name === searchStore.category) || null;
            } else {
                // Reset to initial state if no URL state is found
                layout = initialState.layout;
                searchStore = initialState.search;
                selectedCategory = null;
                currentDetailIndex = null;
            }
        });
    })


    $effect(() => {
        if (!searchStore) {
            return;
        }

        if(appState !== AppState.Success) {
            return;
        }

        if (selectedCategory) {
            searchStore.category = selectedCategory.name;
        } else {
            delete searchStore.category;
        }
    })


    $effect(() => {
        if (appState !== AppState.Success) {
            return;
        }

        SearchEngineUrlState.debounce_save(searchStore, layout, currentDetailIndex ?? undefined);

        if (selectedCategory && selectedCategory.name) {
            loading = true;

            NvbsSearchEngineApi.search(searchStore).then((response) => {
                searchResult = response;
                loading = false;
            })
        }
    })


    $effect(() => {
        if (currentDetailIndex) {
            const r = {
                ...searchStore,
                index: currentDetailIndex
            }
            delete r.page;
            delete r.limit;
            load_detail(r);
            currentDetailHasPrev = currentDetailIndex > 1;
            currentDetailHasNext = currentDetailIndex < searchResult?.totalHits!;
        } else {
            detailResponse = null;
        }
    })

    $effect(() => {
        if (searchStore.category) {
            thumbnailSize = GetThumbnailSize(searchStore.category);
        }
    })

    let lightboxOpen = $state(false);

    function load_detail(detailRequest: DetailQuery) {
        NvbsSearchEngineApi.getDetail(detailRequest).then(async response => {
            saveScrollstate();
            detailResponse = response;
            await tick();
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant'
            });
        })
    }

    async function back() {
        // clear response
        detailResponse = null;
        // clear current detail index
        currentDetailIndex = null;
        // wait for DOM to update
        await tick();

        try {
            restoreScrollstate();
        } catch (error) {
            console.warn("Could not restore scroll position:", error);
        }
    }

    async function setUserForLogging(user: MeResponse) {

        if (!user) {
            return;
        }

        if (!user.id || !user.email || !user.name) {
            console.warn("User data is incomplete, not setting Sentry user.");
            return;
        }

        let data = {
            id: user.id.toString(),
            username: user.name,
            email: user.email,
            has_premium: user.has_premium ? "true" : "false",
        };

        setSentryUser(data);
    }

</script>

<div class="nvbs-searchengine">
    <div class="search-part"
         class:hidden={detailResponse !== null}
    >
        {#if appState === AppState.Error}
            <ErrorMessage/>
        {:else if appState === AppState.Loading}
            <Loading/>
        {:else if appState === AppState.Success}
            <Searchbar
                    value={searchStore.query}
                    infourl={attributes.infourl}
                    search={(search)=>{
                    searchStore.query = search;
                    searchStore.page = 1;
                }}
            />
            {#if !selectedCategory}
                <CategoryBlocks query={searchStore.query} {searchStore} {categories} categorySelected={(category: Category) => {
                selectedCategory = category;
                searchStore.category = category.name;
                searchStore.page = 1;
            }}/>
            {:else}
                <SearchTerms
                        bind:searchStore
                        {selectedCategory}
                        query={searchStore.query}
                        onback={() => {
                        selectedCategory = null;
                        searchStore.category = undefined;
                        searchStore.date_one = undefined;
                        searchStore.date_two = undefined;
                        searchStore.terms = {};
                    }}
                        hasBackButton
                        setSearchTermValues={(values) => {
                        searchStore.terms= values;
                        searchStore.page = 1;
                    }}
                />
                {#if searchResult}
                    <div class="results-container">
                        <Pagination {loading} bind:searchStore searchResults={searchResult}/>
                        <div class="justify-between">
                            <SortSelector bind:searchStore options={sort_options}/>
                            <LayoutSelector bind:layout/>
                        </div>
                        <div class="results"
                             style="--image-size: {thumbnailSize}px"
                             class:results-loading={loading}
                             class:layout-grid={layout === LayoutType.Grid}
                             class:layout-list={layout === LayoutType.List}
                             class:layout-dambord={layout === LayoutType.Dambord}
                        >
                            {#each searchResult.data as post,i (post.id)}
                                <Post
                                        {layout}
                                        category={selectedCategory.name}
                                        postdata={post}
                                        onclick={()=>{
                                    if (post.url) {
                                        let target = "_self";
                                        if(searchStore.category === "nvbs_actueel") {
                                            target = "_blank";
                                        }
                                        window.open(
                                                post.url,
                                                target
                                        );
                                        return;
                                    }
                                    const index = searchStore.limit * (searchStore.page - 1) + i + 1;
                                    currentDetailIndex=index;
                                }}/>
                            {/each}
                        </div>
                        {#if searchResult.data.length >= searchStore.limit!}
                            <Pagination {loading} hideAmount bind:searchStore searchResults={searchResult}/>
                        {/if}
                    </div>
                {/if}
            {/if}
        {/if}
    </div>

    {#if detailResponse}
        <!--{#key detailResponse}-->
        <div class="detail-part">
            <PostDetail
                    bind:lightboxOpen={lightboxOpen}
                    data={detailResponse.data}
                    thumbnail={detailResponse.thumbnail}
                    file={detailResponse.bestand}
                    category={selectedCategory.name}
                    onback={back}
                    onprev={()=>{currentDetailIndex = currentDetailIndex - 1}}
                    onnext={()=>{currentDetailIndex = currentDetailIndex + 1}}
                    hasPrev={currentDetailHasPrev}
                    hasNext={currentDetailHasNext}
                    currentUser={currentUser}
                    copyrighturl={attributes.copyrighturl}
            />
        </div>
        <!--{/key}-->
    {/if}
</div>

<style lang="scss">
  .hidden {
    display: none !important;
  }

  .justify-between {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .nvbs-searchengine {
    width: 100%;
    padding: 2.5rem 0;

    @media (min-width: 768px) {
      padding: 4rem 0;
    }
  }

  .results-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .results {
    display: grid;

    &.layout-grid {
      grid-template-columns: 1fr 1fr;
    }

    &.layout-dambord {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      gap: 20px;
    }

    &.layout-list {
      grid-template-columns: 1fr;
    }
  }

  .results-loading {
    animation: opacity-pulse 1s ease-in-out infinite;
  }

  @keyframes opacity-pulse {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.5;
    }
  }

  // on smaller devices
  @media (max-width: 768px) {
    .results {

      &.layout-grid {
        grid-template-columns: 1fr !important;
      }

      &.layout-dambord {
        grid-template-columns: 1fr 1fr !important;
        gap: 12px;
      }
    }

    .justify-between {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>

